// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import type {RecurringConsent, ShopBillingMode} from '@fluxer/schema/src/domains/premium/PaddleSubscriptionSchemas';
import type {CreateShopOrderRequest} from '@fluxer/schema/src/domains/shop/ShopRequestSchemas';
import type {
	ShopCatalogResponse,
	ShopCodeResponse,
	ShopOrderResponse,
	ShopQuoteResponse,
	ShopUsernameOfferResponse,
	ShopUsernameProductsResponse,
	UsernameCardAvailabilityResponse,
} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';

export async function fetchCatalog(): Promise<ShopCatalogResponse> {
	const response = await http.get<ShopCatalogResponse>(Endpoints.SHOP_CATALOG);
	return response.body;
}

// A username card's name (and the offer it is bought through) prices the quote; without one the server quotes the
// cheapest class of the length.
export interface UsernameQuoteName {
	username: string;
	offer?: string;
}

export async function fetchQuote(
	sku: string,
	billingMode?: ShopBillingMode,
	name?: UsernameQuoteName,
): Promise<ShopQuoteResponse> {
	const query = {
		sku,
		...(billingMode === 'recurring' && {billing_mode: billingMode}),
		...(name && {username: name.username}),
		...(name?.offer !== undefined && {offer: name.offer}),
	};
	const response = await http.get<ShopQuoteResponse>(Endpoints.SHOP_QUOTE, {query});
	return response.body;
}

export interface CreateOrderOptions {
	// A username card also names the username it buys.
	username?: string;
	// A personal offer's code, from its link; only with the username it offers.
	offer?: string;
	// A renewing order (Plus, or N boosts) carries the terms the buyer agreed to, which the server checks against its
	// own price.
	recurringConsent?: RecurringConsent;
	// A one-time Payriff boost order of several 30 day slots; the server prices and limits it.
	quantity?: number;
}

// The client names the product and nothing else: the server prices every order itself.
export async function createOrder(sku: string, options: CreateOrderOptions = {}): Promise<ShopOrderResponse> {
	const body: CreateShopOrderRequest = {
		sku,
		...(options.username !== undefined && {username: options.username}),
		...(options.username !== undefined && options.offer !== undefined && {offer: options.offer}),
		...(options.recurringConsent && {billing_mode: 'recurring', recurring_consent: options.recurringConsent}),
		// A boost subscription names its quantity twice; the server refuses the order unless both agree.
		...(options.recurringConsent?.quantity !== undefined && {quantity: options.recurringConsent.quantity}),
		...(options.quantity !== undefined && {quantity: options.quantity}),
	};
	const response = await http.post<ShopOrderResponse>(Endpoints.SHOP_ORDERS, {body});
	return response.body;
}

// Reads our own order record; the server asks Payriff, the client never does.
export async function fetchOrder(orderId: string): Promise<ShopOrderResponse> {
	const response = await http.get<ShopOrderResponse>(Endpoints.SHOP_ORDER(orderId));
	return response.body;
}

// Display only: the order is checked again when it opens. Never says who has a name.
export async function checkUsernameCard(
	username: string,
	signal: AbortSignal,
): Promise<UsernameCardAvailabilityResponse> {
	const response = await http.get<UsernameCardAvailabilityResponse>(Endpoints.SHOP_USERNAME_AVAILABILITY, {
		query: {username},
		signal,
	});
	return response.body;
}

export async function fetchCode(code: string): Promise<ShopCodeResponse> {
	const response = await http.get<ShopCodeResponse>(Endpoints.SHOP_CODE(code));
	return response.body;
}

// One page of the named usernames on sale, in name order; `after` is the previous page's next_after.
export async function fetchUsernameProducts(after: string | null): Promise<ShopUsernameProductsResponse> {
	const response = await http.get<ShopUsernameProductsResponse>(Endpoints.SHOP_USERNAME_PRODUCTS, {
		query: after === null ? {} : {after},
	});
	return response.body;
}

// The server answers only the account the offer is for; anyone else, an unknown code and an ended offer get one 404.
export async function fetchUsernameOffer(code: string): Promise<ShopUsernameOfferResponse> {
	const response = await http.get<ShopUsernameOfferResponse>(Endpoints.SHOP_USERNAME_OFFER(code));
	return response.body;
}

// The Visionary username right: one free name, once; the server checks the right and the length.
export async function claimVisionaryName(username: string): Promise<ShopOrderResponse> {
	const response = await http.post<ShopOrderResponse>(Endpoints.SHOP_VISIONARY_NAME_CLAIM, {body: {username}});
	return response.body;
}
